/**
 * @file 收集页面的 fmp
 * @author <caierwei@baijia.com>
 * @module module/fmp
 */
import request from './util/request';
import configurable from './util/configurable';
import performance from './util/performance';
import onRouteChange from './util/onRouteChange';

import {
    trackID,
    getUserID,
    Config,
    HB_SESSION_ID_KEY
} from './util/config';
import {
    camelToUnder,
    parse
} from './util/json';
import cookie from './util/cookie';
import {on, off} from './util/dom';
import getPath from './util/getPath';

const win = typeof window !== 'undefined' && window;
const doc = win && document;
const rAF = win.requestAnimationFrame || function raf(callback) {
    win.setTimeout(callback, 1000 / 60);
};

const cAF = win.cancelAnimationFrame || function caf(id) {
    win.clearTimeout(id);
};

const {timing} = performance;

// 当前浏览器支持的 hidden 和 visibilityChange 写法
const [hidden, visibilityChange] = [
    ['hidden', 'visibilitychange'],
    ['webkitHidden', 'webkitvisibilitychange'],
    ['mozHidden', 'mozvisibilitychange'],
    ['msHidden', 'msvisibilitychange']
].filter(([key]) => key in (doc || {}))[0] || [];

/**
 * 收集标记的有意义的元素
 *
 * @const {string}
 */
const LOG_FLAG_N_FMP_HERO = 'data-habo-fmp-hero';

/**
 * 收集标记的有意义的元素的属性名
 *
 * @const {string}
 */
const LOG_FLAG_N_FMP_PARAMS = 'data-habo-fmp-params';

const HB_SESSION_ID_VALUE = cookie.get(HB_SESSION_ID_KEY);

/**
 * 性能上报
 *
 * @function
 * @param {Object} json 要上报的数据
 * @param {Function=} callback 上报后的回调
 */
const report = configurable((json, callback) => {
    const {
        url = location.href,
        uuid = trackID,
        uid = getUserID(),
        ...rest
    } = camelToUnder(json);

    const {
        type: nt = 'unknown',
        effectiveType: net = '',
        rtt: nrtt = 0,
        downlinkMax: ndlm = 0,
        downlink: ndl = 0
    } = typeof navigator !== 'undefined' && navigator.connection || {};

    // 要上报的数据
    const perfData = {
    // 当前页面 URL
        url,

        // 用户的唯一标识，由后端生成写到 cookie 中
        uuid,

        // 登录用户 ID，用户登录后端写到 cookie 中
        uid,

        path: getPath(),
        // 页面路径归类
        action_name: getPath(),
        app_nid: Config.APPNID || Config.appNid || Config.appId,
        [HB_SESSION_ID_KEY]: HB_SESSION_ID_VALUE || cookie.get(HB_SESSION_ID_KEY),
        start_time: Date.now(),
        // 网络相关
        nt,
        net,
        nrtt,
        ndlm,
        ndl,
        ...rest
    };

    request(Config.VERB)(`${Config.TIME}`, perfData, callback);
    // 上报到 perf 大数据
    request(Config.VERB)(`${Config.PERF}`, {
        ...perfData,
        only_big_data: true,
    }, callback);
}, {});

class FMPCalculator {
    // 当前观测的关键元素
    currentObserverDom = null;

    // 是否有路由切换
    routeChange = false;

    // 是否已经上报
    hasReport = false;

    // 观测开始事件
    observeBeginTime = timing?.navigationStart || timing?.fetchStart || Date.now();

    timerId = null;

    constructor(args) {
        this.config = {
            ...this.config,
            ...args
        };
    }

    // 默认配置
    config = {
        container: document
    };

    /**
     * 获取 FMPCalculator 实例
     *
     * @function getInstance
     * @param {?Object=} [ {container: document} ] 自定义配置值
     * @return {Object} fmpCalculator 实例
     */
    static getInstance(conf) {
        if (!FMPCalculator.instance) {
            FMPCalculator.instance = new FMPCalculator({...conf});
        }
        return FMPCalculator.instance;
    }

    /**
     * 重置观测初始值
     *
     * @function resetObserve
     */
    resetObserve() {
        // 切换路由，还没上报就取消上报吧
        this.cancelReport();
        this.hasReport = false;
        this.observeBeginTime = Date.now();

        if (this.currentObserverDom) {
            this.currentObserverDom?.disconnect?.();
            this.currentObserverDom = null;
        }
        this.initObserve();
    }

    /**
     * 初始化观测，开始观测标记元素
     *
     * @function resetObserve
     * @return {Object} 返回当前 MutationObserver 对象
     */
    initObserve() {
        try {
            this.currentObserverDom = new MutationObserver(mutationRecords => {
                for (const record of mutationRecords) {
                    if (record && record.addedNodes) {
                        for (const node of record.addedNodes) {
                            if (1 === node.nodeType
                                && (node.hasAttribute(LOG_FLAG_N_FMP_HERO)
                                    || node.querySelector(`[${LOG_FLAG_N_FMP_HERO}]`))
                            ) {
                                const timeByNoRaf = Date.now() - this.observeBeginTime;
                                this.timerId = rAF(() => {
                                    const logFmpParams = node.getAttribute(LOG_FLAG_N_FMP_PARAMS);
                                    const fmpParams = logFmpParams ? parse(logFmpParams) : {};
                                    const time = Date.now() - this.observeBeginTime;
                                    const type = this.routeChange ? 'rmp' : 'fmp';
                                    const {onReport} = this.config;
                                    const perfData = {
                                        ...fmpParams,
                                        req_type: type,
                                        elapsed: parseInt(String(time), 10),
                                        [type]: parseInt(String(time), 10),
                                        time_by_no_raf: parseInt(String(timeByNoRaf), 10),
                                    };
                                    const callback = () => typeof onReport === 'function' && onReport(perfData);
                                    if (!this.hasReport) {
                                        report(perfData, callback);
                                        this.hasReport = true;
                                    }
                                });
                                this.currentObserverDom?.disconnect();
                                break;
                            }
                        }
                    }
                    if (this.hasReport) {
                        break;
                    }
                }
            });

            this.currentObserverDom?.observe(this.config.container || document, {
                childList: true,
                subtree: true
            });

            return this.currentObserverDom;
        }
        catch (error) {
            console.error('not support MutationObserver');
        }

    }

    /**
     * 开始 fmp/rmp 观测
     *
     * @function resetObserve
     */
    startObserve() {
        this.teardown = onRouteChange(e => {
            const {isTrusted, habo} = e;
            if (isTrusted || (habo && !isTrusted)) {
                this.routeChange = true;
                this.resetObserve();
            }
        });
        this.initObserve();
    }

    /**
     * 停止 fmp/rmp 观测
     *
     * @function resetObserve
     */
    stopObserve() {
        this.cancelReport();
        if (typeof this.teardown === 'function') {
            this.teardown();
        }
    }

    /**
     * 切了后台，取消上报。
     *
     */
    cancelReport() {
        if (this.timerId) {
            cAF(this.timerId);
            this.timerId = null;
        }
        this.hasReport = true;
        this.currentObserverDom?.disconnect();
        this.currentObserverDom = null;
    }
}

const monitorFmp = () => {
    FMPCalculator.getInstance().cancelReport();
};

/**
 * 解除相关事件的绑定
*/
function destroy() {
    off(win, 'unload', destroy);
    FMPCalculator.getInstance().stopObserve();
    off(doc, visibilityChange, monitorFmp);
}

report.init = (conf = {}) => {
    FMPCalculator.getInstance(conf).startObserve();
    on(win, 'unload', destroy);
    on(doc, visibilityChange, monitorFmp);
};

export default report;
